import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Download, ArrowLeft, Calendar } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface EventRegistration {
  id: string;
  full_name: string;
  email: string;
  event_slug: string;
  registered_at: string;
}

export function EventRegistrations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    checkAndLoad();
  }, [user, navigate]);

  const checkAndLoad = async () => {
    if (!user?.id) return;
    const isAdmin = await isUserAdmin(user.id);
    if (!isAdmin) { navigate('/login'); return; }

    const { data } = await supabase
      .from('event_registrations')
      .select('*')
      .order('registered_at', { ascending: false });

    setRegistrations(data || []);
    setIsLoading(false);
  };

  const downloadCSV = () => {
    const rows = [['Full Name', 'Email', 'Event', 'Registered At']];
    filtered.forEach(r => {
      rows.push([
        r.full_name,
        r.email,
        r.event_slug,
        new Date(r.registered_at).toLocaleString(),
      ]);
    });
    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'event-registrations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = registrations.filter(r =>
    r.full_name.toLowerCase().includes(search.toLowerCase()) ||
    r.email.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) return <LoadingSpinner message="Loading registrations..." />;

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="p-2 hover:bg-gray-10 rounded-lg transition-colors text-gray-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-teal-600 rounded-xl shadow-soft">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">Event Registrations</h1>
              <p className="text-text-sm text-gray-40">Day 2 — Foundations Certification</p>
            </div>
          </div>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Summary card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="freud-card-elevated p-5 mb-6 flex items-center gap-4"
        >
          <div className="p-3 bg-teal-50 rounded-xl">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <div className="text-heading-sm text-gray-80">{registrations.length}</div>
            <div className="text-text-sm text-gray-40">Total Registrations</div>
          </div>
        </motion.div>

        {/* Search */}
        <div className="mb-5">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full sm:w-80 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
          />
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="freud-card-elevated overflow-hidden"
        >
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-40 text-sm">
              {search ? 'No results match your search.' : 'No registrations yet.'}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-50 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-50 uppercase tracking-wide">
                    Email
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-50 uppercase tracking-wide hidden sm:table-cell">
                    Registered
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((r, i) => (
                  <motion.tr
                    key={r.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-5 py-3.5 text-sm text-gray-80 font-medium">
                      {r.full_name}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-60">
                      {r.email}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-40 hidden sm:table-cell whitespace-nowrap">
                      {new Date(r.registered_at).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      </main>
    </div>
  );
}
